import { useState, useEffect, useRef, useCallback } from "react";
import { Search, Sparkles, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";

export default function BookAutocomplete({ value, onChange, onPick }) {
    const { t, i18n } = useTranslation();
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [highlight, setHighlight] = useState(-1);
    const [loading, setLoading] = useState(false);
    const wrapperRef = useRef(null);
    const abortRef = useRef(null);

    const fetchSuggestions = useCallback(async (q) => {
        if (!q || q.trim().length < 2) {
            setSuggestions([]);
            setLoading(false);
            return;
        }
        if (abortRef.current) abortRef.current.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        setLoading(true);
        try {
            // Pass `lang` so Google Books can prefer Spanish results when
            // the user is browsing in Spanish (and English when in English).
            // Falls through gracefully to language-agnostic results if the
            // backend doesn't honour it.
            const lang = (i18n.language || "es").split("-")[0];
            const { data } = await api.get("/books/search", {
                params: { q, limit: 6, lang },
                signal: controller.signal,
            });
            setSuggestions(Array.isArray(data) ? data : []);
            setHighlight(-1);
        } catch (err) {
            if (err.name !== "CanceledError" && err.name !== "AbortError") {
                setSuggestions([]);
            }
        } finally {
            setLoading(false);
        }
    }, [i18n.language]);

    useEffect(() => {
        // 350ms debounce — comfortable for fast typists without firing
        // too many cancelled requests. Combined with a 2.5s OL timeout,
        // worst-case visible loading time is ~3s for slow OL responses.
        const t1 = setTimeout(() => fetchSuggestions(value), 350);
        return () => clearTimeout(t1);
    }, [value, fetchSuggestions]);

    useEffect(() => {
        const handler = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const handlePick = (item) => {
        onChange(item.title);
        setOpen(false);
        setSuggestions([]);
        if (onPick) onPick(item);
    };

    const handleKeyDown = (e) => {
        if (!open || !suggestions.length) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, -1));
        } else if (e.key === "Enter" && highlight >= 0) {
            e.preventDefault();
            handlePick(suggestions[highlight]);
        } else if (e.key === "Escape") {
            setOpen(false);
        }
    };

    return (
        <div className="relative" ref={wrapperRef}>
            <div className="relative flex items-end border-b border-[var(--ink)] focus-within:border-[var(--accent)] transition-colors">
                <Search
                    size={24}
                    strokeWidth={1.5}
                    className="mb-4 mr-3 text-[var(--ink-dim)] flex-shrink-0"
                />
                <input
                    data-testid="book-title-input"
                    type="text"
                    autoFocus
                    value={value}
                    onChange={(e) => {
                        onChange(e.target.value);
                        setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={handleKeyDown}
                    placeholder={t("home.search_placeholder")}
                    className="flex-1 bg-transparent border-0 outline-none font-display text-3xl md:text-5xl text-[var(--ink)] py-3 md:py-4"
                    style={{ fontVariationSettings: '"wght" 500, "wdth" 90, "opsz" 96' }}
                    maxLength={300}
                    autoComplete="off"
                    role="combobox"
                    aria-autocomplete="list"
                    aria-expanded={open && suggestions.length > 0}
                />
                {loading && (
                    <span className="mb-5 ml-2 h-1.5 w-1.5 bg-[var(--accent)] rounded-full animate-slow-pulse" />
                )}
            </div>

            {open && suggestions.length > 0 && (
                <ul
                    data-testid="autocomplete-list"
                    role="listbox"
                    className="absolute left-0 right-0 top-full mt-2 z-30 bg-[var(--bg-elevated)] border border-[var(--border-strong)] shadow-2xl max-h-[70vh] overflow-auto"
                >
                    <li
                        aria-hidden="true"
                        className="px-4 py-2 text-[10px] uppercase tracking-[0.3em] text-[var(--accent)] border-b border-[var(--border)] flex items-center gap-2 bg-[var(--bg)]"
                    >
                        <Sparkles size={11} strokeWidth={1.8} />
                        {t("autocomplete.tap_to_recommend")}
                    </li>
                    {suggestions.map((s, i) => {
                        const active = i === highlight;
                        return (
                            <li
                                key={`${s.title}-${s.author}-${i}`}
                                role="option"
                                aria-selected={active}
                                data-testid={`autocomplete-option-${i}`}
                                onMouseEnter={() => setHighlight(i)}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    handlePick(s);
                                }}
                                className={`flex items-center gap-3 px-4 py-3 cursor-pointer border-b border-[var(--border)] last:border-b-0 transition-colors group ${
                                    active ? "bg-[var(--bg-hover)]" : "hover:bg-[var(--bg-hover)]"
                                }`}
                            >
                                {s.cover ? (
                                    <img
                                        src={s.cover}
                                        alt=""
                                        className="w-12 h-16 object-cover border border-[var(--border-strong)] flex-shrink-0 shadow-sm"
                                        loading="lazy"
                                    />
                                ) : (
                                    <div className="w-12 h-16 bg-[var(--bg-strong)] border border-[var(--border-strong)] flex-shrink-0" />
                                )}
                                <div className="min-w-0 flex-1">
                                    <p className="font-display text-lg text-[var(--ink)] leading-tight truncate"
                                       style={{ fontVariationSettings: '"wght" 600, "wdth" 100, "opsz" 24' }}>
                                        {s.title}
                                    </p>
                                    {s.author && (
                                        <p className="text-xs text-[var(--ink-muted)] truncate mt-0.5">
                                            {s.author}
                                        </p>
                                    )}
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    {s.year && (
                                        <span className="text-[10px] uppercase tracking-widest font-mono-num text-[var(--ink-dim)] border border-[var(--border)] rounded px-1.5 py-0.5">
                                            {s.year}
                                        </span>
                                    )}
                                    <ArrowRight
                                        size={14}
                                        strokeWidth={1.8}
                                        className={`text-[var(--accent)] transition-transform ${
                                            active ? "translate-x-0.5" : "opacity-0 group-hover:opacity-100"
                                        }`}
                                    />
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
